import React, { useState, useEffect } from "react";
import axios from "axios";
import colors from "../Constants/colors";

const PrepareResult = () => {
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [subjectMarks, setSubjectMarks] = useState({});
  const [scores, setScores] = useState({});
  const [remarks, setRemarks] = useState({});
  const [examResults, setExamResults] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingResultId, setEditingResultId] = useState("");
  const [teacherClassIncharge, setTeacherClassIncharge] = useState("");
  const [assignedClass, setAssignedClass] = useState("");
  const [assignedSection, setAssignedSection] = useState("");

  const labelStyle = "text-black font-semibold mb-1 !text-[18px]";
  const inputStyle =
    "border rounded-lg px-3 py-2 outline-none !text-[14px] placeholder:!text-[14px] focus:ring-2 focus:ring-blue-500";

  const teacherData = JSON.parse(localStorage.getItem("teacher"));
  const teacherId = teacherData?.id;
  const sessionId = localStorage.getItem("currentSessionId");

  // Fetch teacher info
  useEffect(() => {
    const fetchTeacherInfo = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/teachers/getTeacherById/${teacherId}`,
          { params: { sessionId } }
        );
        const teacherData = response.data;
if (teacherData.classIncharge) {
  const classIncharge = teacherData.classIncharge.trim(); // e.g. "1st - A"

  // Split by " - " if parentheses not used
  if (classIncharge.includes(" - ")) {
    const [className, sectionName] = classIncharge.split(" - ");
    setAssignedClass(className.trim());    // "1st"
    setAssignedSection(sectionName.trim()); // "A"
    setTeacherClassIncharge(classIncharge);
  } else {
    // Match "Class (Section)" pattern
    const match = classIncharge.match(/^(.+?)\s*\((.+)\)$/);
    if (match) {
      const className = match[1].trim();   // "5th"
      const sectionName = match[2].trim(); // "A"
      setAssignedClass(className);
      setAssignedSection(sectionName);
      setTeacherClassIncharge(classIncharge);
    } else {
      // fallback if format is not as expected
      setAssignedClass(classIncharge);
      setAssignedSection("");
      setTeacherClassIncharge(classIncharge);
    }
  }
}


      } catch (error) {
        console.error("Error fetching teacher data:", error);
      }
    };
    fetchTeacherInfo();
  }, [teacherId]);

  // Fetch exams, students, subjects, and results
  useEffect(() => {
    if (!assignedClass) return;

    // Fetch exams
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/exams/getExamByClass/${assignedClass}`, { params: { sessionId } })
      .then((res) => setExams(res.data))
      .catch((err) => console.error("Error fetching exams:", err));

    // Fetch students
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, { params: { sessionId } })
      .then((res) => {
        const filtered = res.data.filter(
          (s) => s.class === assignedClass && s.section === assignedSection
        );
        setStudents(filtered);
      })
      .catch((err) => console.error("Error fetching students:", err));

    // Fetch subjects
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/subjects/getSubjectsByClassName/${assignedClass}`, { params: { sessionId } })
      .then((res) => {
        setSubjects(res.data);
        const marks = {};
        res.data.forEach((s) => (marks[s.subjectName] = s.totalMarks || 0));
        setSubjectMarks(marks);
      })
      .catch((err) => console.error("Error fetching subjects:", err));

    // Fetch all exam results
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/examresults/getAllExamResults`, { params: { sessionId } })
      .then((res) => setExamResults(res.data))
      .catch((err) => console.error("Error fetching exam results:", err));
  }, [assignedClass, assignedSection]);

  const handleSubjectMarksChange = (subject, value) => {
    setSubjectMarks((prev) => ({ ...prev, [subject]: value }));
  };

  const handleScoreChange = (id, subject, value) => {
    setScores((prev) => ({
      ...prev,
      [id]: { ...prev[id], [subject]: value },
    }));
  };

  const handleRemarkChange = (id, value) => {
    setRemarks((prev) => ({ ...prev, [id]: value }));
  };

  const calculateTotal = (id) =>
    Object.values(scores[id] || {}).reduce((sum, s) => sum + Number(s || 0), 0);

  const calculatePercentage = (id) => {
    const total = calculateTotal(id);
    const max = Object.values(subjectMarks).reduce((a, b) => a + Number(b || 0), 0);
    return max ? ((total / max) * 100).toFixed(2) : "0.00";
  };

  const handleSubmit = async () => {
    const data = {
      examName: selectedExam,
      className: assignedClass,
      sectionName: assignedSection,
      sessionId, 
      subjects: subjects.map((s) => ({
        subjectName: s.subjectName,
        totalMarks: subjectMarks[s.subjectName] || 0,
      })),
      students: students.map((st) => ({
        studentId: st._id,
        rollNo: st.rollNo,
        studentName: `${st.firstName} ${st.lastName}`,
        scores: Object.keys(subjectMarks).map((sub) => ({
          subjectName: sub,
          score: scores[st._id]?.[sub] || 0,
        })),
        remark: remarks[st._id] || "",
      })),
    };

    try {
      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/examresults/updateExamResult/${editingResultId}`,
          data,
          { params: { sessionId } } 
        );
        alert("Result updated successfully!");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/examresults/createExamResult`,
          data,
          { params: { sessionId } } 
        );
        alert("Result submitted successfully!");
      }
      setIsEditing(false);
      setEditingResultId("");
    } catch (err) {
      console.error("Error saving result:", err.response?.data || err);
    }
  };

  const handleEdit = (id) => {
    const res = examResults.find((r) => r._id === id);
    if (!res) return;
    setSelectedExam(res.examName);
    setIsEditing(true);
    setEditingResultId(id);

    // Populate existing scores and remarks
    const loadedScores = {};
    res.students.forEach((st) => {
      loadedScores[st.studentId] = {};
      st.scores.forEach((sub) => {
        loadedScores[st.studentId][sub.subjectName] = sub.score;
      });
    });
    setScores(loadedScores);

    const loadedRemarks = {};
    res.students.forEach((st) => {
      loadedRemarks[st.studentId] = st.remark;
    });
    setRemarks(loadedRemarks);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this result?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/examresults/deleteExamResult/${id}`, {
        params: { sessionId }, 
      });
      setExamResults((prev) => prev.filter((r) => r._id !== id));
      alert("Result deleted!");
    } catch (err) {
      console.error("Error deleting result:", err);
    }
  };

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <i className="fas fa-tasks text-blue-600 !text-[20px]" />
          <h1 className="font-bold !text-[18px]">Prepare Exam Results</h1>
        </div>
      </div>
      <p className="text-gray-600 mb-6 !text-[14px]">
        {teacherClassIncharge
          ? `Class Incharge: ${teacherClassIncharge}`
          : "No class assigned."}
      </p>

      {/* Exam Selection Card */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <i className="fas fa-clipboard-check text-blue-600 !text-[18px]" />
          <h2 className="font-semibold !text-[18px] text-gray-800">
            Select Exam & Enter Marks
          </h2>
        </div>

        <div className="flex flex-col">
          <label className={labelStyle}>Exam Name:</label>
          <select
            value={selectedExam}
            onChange={(e) => setSelectedExam(e.target.value)}
            className={inputStyle}
          >
            <option value="">Select Exam</option>
            {exams.map((exam) => (
              <option key={exam._id} value={exam.examName}>
                {exam.examName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Subjects & Total Marks Card */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <i className="fas fa-book text-purple-600 !text-[18px]" />
          <h2 className="font-semibold !text-[18px] text-gray-800">
            Subjects & Total Marks
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 text-center !text-[14px] bg-white">
            <thead className="bg-gray-100 text-gray-800">
              <tr>
                <th className="border px-4 py-2 font-semibold">Subject</th>
                <th className="border px-4 py-2 font-semibold">
                  Total Marks
                </th>
              </tr>
            </thead>
            <tbody>
              {subjects.map((sub) => (
                <tr key={sub._id} className="border-b">
                  <td className="border px-4 py-2">{sub.subjectName}</td>
                  <td className="border px-4 py-2">
                    <input
                      type="number"
                      value={subjectMarks[sub.subjectName] || ""}
                      onChange={(e) =>
                        handleSubjectMarksChange(
                          sub.subjectName,
                          e.target.value
                        )
                      }
                      className={`${inputStyle} !px-2 !py-1`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Student Scores Card */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <i className="fas fa-user-graduate text-green-600 !text-[18px]" />
          <h2 className="font-semibold !text-[18px] text-gray-800">
            Student Scores
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 text-center !text-[14px] bg-white">
            <thead className="bg-gray-100 text-gray-800">
              <tr>
                <th className="border px-3 py-2 font-semibold">#</th>
                <th className="border px-3 py-2 font-semibold">Roll No</th>
                <th className="border px-3 py-2 font-semibold">Student</th>
                {subjects.map((s) => (
                  <th
                    key={s._id}
                    className="border px-3 py-2 font-semibold"
                  >
                    {s.subjectName}
                  </th>
                ))}
                <th className="border px-3 py-2 font-semibold">Total</th>
                <th className="border px-3 py-2 font-semibold">%</th>
                <th className="border px-3 py-2 font-semibold">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {students.map((stu, i) => (
                <tr key={stu._id} className="border-b">
                  <td className="border px-3 py-2">{i + 1}</td>
                  <td className="border px-3 py-2">{stu.rollNo}</td>
                  <td className="border px-3 py-2">
                    {stu.firstName} {stu.lastName}
                  </td>
                  {subjects.map((s) => (
                    <td key={s._id} className="border px-3 py-2">
                      <input
                        type="number"
                        value={scores[stu._id]?.[s.subjectName] || ""}
                        onChange={(e) =>
                          handleScoreChange(
                            stu._id,
                            s.subjectName,
                            e.target.value
                          )
                        }
                        className={`${inputStyle} !px-2 !py-1`}
                      />
                    </td>
                  ))}
                  <td className="border px-3 py-2">
                    {calculateTotal(stu._id)}
                  </td>
                  <td className="border px-3 py-2">
                    {calculatePercentage(stu._id)}%
                  </td>
                  <td className="border px-3 py-2">
                    <input
                      type="text"
                      value={remarks[stu._id] || ""}
                      onChange={(e) =>
                        handleRemarkChange(stu._id, e.target.value)
                      }
                      className={`${inputStyle} !px-2 !py-1`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-4 mb-8">
        {isEditing && (
          <button
            onClick={() => {
              setIsEditing(false);
              setEditingResultId("");
            }}
            className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg !text-[14px]"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmit}
          className="text-white px-4 py-2 rounded-lg !text-[14px] hover:opacity-90"
          style={{ background: colors.gradientPrimary }}
        >
          {isEditing ? "Update Result" : "Save Result"}
        </button>
      </div>

      {/* All Results Card */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <i className="fas fa-table text-green-600 !text-[18px]" />
          <h2 className="font-semibold !text-[18px] text-gray-800">
            All Exam Results
          </h2>
        </div>

        {examResults.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300 text-center !text-[14px] bg-white">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="border px-4 py-2 font-semibold">#</th>
                  <th className="border px-4 py-2 font-semibold">Exam</th>
                  <th className="border px-4 py-2 font-semibold">
                    Class
                  </th>
                  <th className="border px-4 py-2 font-semibold">
                    Section
                  </th>
                  <th className="border px-4 py-2 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {examResults.map((res, i) => (
                  <tr
                    key={res._id}
                    className="border-b hover:bg-gray-50"
                  >
                    <td className="border px-4 py-2">{i + 1}</td>
                    <td className="border px-4 py-2">
                      {res.examName}
                    </td>
                    <td className="border px-4 py-2">
                      {res.className}
                    </td>
                    <td className="border px-4 py-2">
                      {res.sectionName}
                    </td>
                    <td className="border px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleEdit(res._id)}
                          className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-lg !text-[13px]"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(res._id)}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg !text-[13px]"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-600 !text-[14px]">
            No results found.
          </p>
        )}
      </div>
    </div>
  );
};

export default PrepareResult;
